import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { env } from "../env/env";
import BrandKitApi from "../api/BrandKitApi";
import { brandKitsState, memberState } from "../state/atoms";
import { BrandKitDto, LocationDto } from "../generated/dashboard-client";
import { MemberState } from "../state/atoms";

export function canManageBrand(member?: MemberState | null): boolean {
  return !!member && !!(member.owner || member.role === "Admin" || (member as any).manageBrand);
}

function brandKitForLocation(
  kits: BrandKitDto[],
  location?: LocationDto | null
): BrandKitDto | undefined {
  const id = location?.internalId;
  if (id == null) return undefined;
  return kits.find((k) => (k.locationIds || []).includes(id));
}

export function useBrandDestination() {
  const member = useRecoilValue(memberState);
  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);
  const navigate = useNavigate();
  const teamId = member?.teamId;
  const fetching = useRef<Promise<BrandKitDto[]> | null>(null);
  const canManage = canManageBrand(member);

  const ensureKits = useCallback((): Promise<BrandKitDto[]> => {
    if (brandKits.length > 0) return Promise.resolve(brandKits);
    if (!teamId || !canManage) return Promise.resolve([]);
    if (!fetching.current) {
      fetching.current = BrandKitApi.listForTeam(teamId)
        .then((resp) => {
          const kits = resp.data || [];
          setBrandKits(kits);
          return kits;
        })
        .catch(() => [] as BrandKitDto[])
        .finally(() => {
          fetching.current = null;
        });
    }
    return fetching.current;
  }, [brandKits, teamId, canManage, setBrandKits]);

  const destinationFor = useCallback(
    (location?: LocationDto | null, kits: BrandKitDto[] = brandKits): string => {
      if (!canManage) {
        return `${env.dashboardPath}/brand/${location?.internalId ?? ""}`;
      }
      const kit = brandKitForLocation(kits, location);
      return kit?.id
        ? `${env.dashboardPath}/account/brand-management/${kit.id}`
        : `${env.dashboardPath}/account/brand-management`;
    },
    [canManage, brandKits]
  );

  const goToBrand = useCallback(
    async (location?: LocationDto | null) => {
      const kits = canManage ? await ensureKits() : brandKits;
      navigate(destinationFor(location, kits));
    },
    [canManage, ensureKits, brandKits, destinationFor, navigate]
  );

  return { destinationFor, goToBrand, ensureKits, canManage };
}
